import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { IBookingV4ServiceItem, IBookingV4StaffItem } from '../types';

dayjs.extend(customParseFormat);

const TIME_FORMAT = 'HH:mm';

// "09:00" -> "09:00AM"
export const formatTimeLabel = (time?: string): string => {
  if (!time) return '';
  const parsed = dayjs(time, TIME_FORMAT);
  if (!parsed.isValid()) return '';
  return parsed.format('hh:mmA');
};

export const addMinutes = (time: string, minutes: number): string =>
  dayjs(time, TIME_FORMAT).add(minutes, 'minute').format(TIME_FORMAT);

// master* overrides win over base values for both display and submit.
export const getCurrentStartTime = (service: IBookingV4ServiceItem): string =>
  service.masterStartTime ?? service.startTime;

export const getCurrentDuration = (service: IBookingV4ServiceItem): number =>
  service.masterDuration ?? service.duration;

// Range label for a single service, e.g. "09:00AM - 09:30AM".
export const getServiceRangeLabel = (service: IBookingV4ServiceItem): string => {
  const start = getCurrentStartTime(service);
  if (!start) return '';
  const end = addMinutes(start, getCurrentDuration(service));
  return `${formatTimeLabel(start)} - ${formatTimeLabel(end)}`;
};

// Range label spanning all services of a staff block (first start -> last end).
export const getStaffRangeLabel = (staff: IBookingV4StaffItem): string => {
  const services = staff.services.filter((s) => getCurrentStartTime(s));
  if (!services.length) return '';
  const first = services[0];
  const last = services[services.length - 1];
  const end = addMinutes(getCurrentStartTime(last), getCurrentDuration(last));
  return `${formatTimeLabel(getCurrentStartTime(first))} - ${formatTimeLabel(end)}`;
};

// Re-sequence service start times consecutively from a block start time.
export const resequenceServices = (
  services: IBookingV4ServiceItem[],
  startTime: string
): IBookingV4ServiceItem[] => {
  let cursor = startTime;
  return services.map((service) => {
    const next: IBookingV4ServiceItem = { ...service, startTime: cursor };
    cursor = addMinutes(cursor, getCurrentDuration(service));
    return next;
  });
};

export const getStaffStartTime = (staff: IBookingV4StaffItem): string =>
  staff.services.map(getCurrentStartTime).find(Boolean) || '';

const DISPLAY_FORMAT = 'hh:mm A';

// "09:00" -> "09:00 AM" (matches the edit-start-time slot list format)
export const toDisplayTime = (time?: string): string => {
  if (!time) return '';
  const parsed = dayjs(time, TIME_FORMAT);
  return parsed.isValid() ? parsed.format(DISPLAY_FORMAT) : '';
};

// "09:00 AM" -> "09:00"
export const fromDisplayTime = (display?: string): string => {
  if (!display) return '';
  const parsed = dayjs(display, DISPLAY_FORMAT);
  return parsed.isValid() ? parsed.format(TIME_FORMAT) : '';
};
